import {
  fetchPushPublicKey,
  removePushSubscription,
  savePushSubscription,
} from '../api/notificationsApi';

const WORKER_URL = '/notification-sw.js';
const OPT_OUT_KEY = 'obana-admin-push-opt-out';

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const pushPermission = () =>
  isPushSupported() ? window.Notification.permission : 'unsupported';

const hasOptedOut = () => {
  try {
    return window.localStorage.getItem(OPT_OUT_KEY) === '1';
  } catch {
    return false;
  }
};

const setOptOut = (value) => {
  try {
    if (value) window.localStorage.setItem(OPT_OUT_KEY, '1');
    else window.localStorage.removeItem(OPT_OUT_KEY);
  } catch {
    return;
  }
};

const urlBase64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = window.atob(
    (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  );
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
};

const usesKey = (subscription, publicKey) => {
  const current = subscription?.options?.applicationServerKey;
  if (!current) return true;
  const existing = new Uint8Array(current);
  const expected = urlBase64ToUint8Array(publicKey);
  return (
    existing.length === expected.length &&
    existing.every((byte, index) => byte === expected[index])
  );
};

export const registerNotificationWorker = async () => {
  if (!isPushSupported()) return null;
  await navigator.serviceWorker.register(WORKER_URL);
  return navigator.serviceWorker.ready;
};

const WORKER_READY_TIMEOUT_MS = 3000;

export const getPushSubscription = async () => {
  if (!isPushSupported()) return null;
  const ready = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise((resolve) => setTimeout(() => resolve(null), WORKER_READY_TIMEOUT_MS)),
  ]);
  const registration = ready || (await navigator.serviceWorker.getRegistration('/'));
  return registration ? registration.pushManager.getSubscription() : null;
};

const subscribeAndSave = async () => {
  const { configured, public_key: publicKey } = await fetchPushPublicKey();
  if (!configured || !publicKey) {
    throw new Error('Push notifications are not configured on the server yet.');
  }

  const registration = await registerNotificationWorker();
  let subscription = await registration.pushManager.getSubscription();
  if (subscription && !usesKey(subscription, publicKey)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  return savePushSubscription(subscription.toJSON());
};

export const enablePushOnThisDevice = async () => {
  if (!isPushSupported()) {
    throw new Error('This browser does not support push notifications.');
  }
  const permission = await window.Notification.requestPermission();
  if (permission === 'denied') {
    throw new Error(
      'Notifications are blocked for this site. Allow them in your browser site settings, then try again.'
    );
  }
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted.');
  }
  const result = await subscribeAndSave();
  setOptOut(false);
  return result;
};

export const disablePushOnThisDevice = async ({ optOut = true } = {}) => {
  if (optOut) setOptOut(true);
  const subscription = await getPushSubscription().catch(() => null);
  if (!subscription) return null;
  const result = await removePushSubscription(subscription.endpoint).catch(
    () => null
  );
  await subscription.unsubscribe().catch(() => false);
  return result;
};

export const syncPushSubscription = async () => {
  if (!isPushSupported() || pushPermission() !== 'granted' || hasOptedOut()) {
    return null;
  }
  return subscribeAndSave();
};
